import { ref, computed, watch, onBeforeUnmount, toValue } from 'vue'
import { type MaybeRefOrGetter, type Ref } from 'vue'

import { useAdaStore } from '../stores/ada'
import { useDialogStore } from '../stores/dialog'
import { yieldToMain } from '../utils/scheduler'

// how many ad slots we push to MoltenBundle in one go before yielding back to the main thread,
// so a pending user input/paint doesn't get stuck behind this whole loop
const AD_SLOT_PUSH_CHUNK_SIZE = 4

import type { ComposedMetaData, ComposedPageMeta } from '@xcel-next/composer-schema'
import type { BxMbWindow, MoltenBundlePosition } from '../types'
import type { AdSlotReloadConfig } from '@xcel-next/rendering-tools'

type AutoReloadConfig = {
  allowedSlots: string[],
  slotStatus: Record<string, { hasViewability: boolean, isVisible: boolean }>,
  lastActivityTime: number | null,
  activityEvents: string[],
  reloadInterval: number,
  reloadIntervalId: ReturnType<typeof setInterval> | null,
  viewability: { percentage: number, time: number }
}

export function shouldReloadAdsOnInteraction (params: AdSlotReloadConfig, clickCount: number, secondsSinceLastReload: number): boolean {
  if (secondsSinceLastReload < params.minDisplayDuration) return false
  return params.clickCount <= 0 || clickCount % params.clickCount === 0
}

export function useAdaMoltenBundle (
  { metaData, pageMeta, registerSlots, deviceType }: { metaData?: MaybeRefOrGetter<ComposedMetaData | undefined>, pageMeta?: MaybeRefOrGetter<ComposedPageMeta | undefined>, registerSlots: Ref<string[]>, deviceType: Ref<string | null> }) {
  const adaStore = useAdaStore()
  const dialogStore = useDialogStore()
  const bxWindow = (typeof window !== 'undefined' ? window : undefined) as unknown as BxMbWindow

  const reloadSlots = ref<{ onInteraction: string[], automatically: string[] }>({ onInteraction: [], automatically: [] })
  let reloadTriggerTime = Math.round(+new Date() / 1000)
  const clickEventCounter = ref(0)
  const autonativeSlots = ['superbanner_1', 'skyscraper_1', 'wallpaper_1', 'mobile_1']
  const asyncContentReloadParams = ref<AdSlotReloadConfig | null>(null)
  const asyncContentReloadCooldown = 10 // seconds, to prevent multiple reloads in a short time

  const autoReload = ref<AutoReloadConfig>({
    allowedSlots: [],
    slotStatus: {},
    lastActivityTime: null,
    activityEvents: ['mousemove', 'keypress', 'scroll', 'touchstart', 'touchend'],
    reloadInterval: Number.MAX_SAFE_INTEGER, // 30, // define the interval in seconds
    reloadIntervalId: null,
    viewability: {
      percentage: 50, // visible percentage
      time: 1 // in seconds
    }
  })

  const autoReloadEnabled = computed(() => reloadSlots.value.automatically.length > 0)

  watch(() => adaStore.isAsyncContentReadyForAdReload, (newVal) => {
    if (newVal && asyncContentReloadParams.value) {
      adaSlotReloadOnInteraction(asyncContentReloadParams.value)
      asyncContentReloadParams.value = null
    }
  })

  onBeforeUnmount(() => {
    clearInterval(autoReload.value.reloadIntervalId ?? undefined)
    toggleActivityListeners(true)
  })

  function initMoltenBundle () {
    const meta = toValue(metaData)
    if (!meta?.adaAutoNative) {
      initAutoReload()
    }
    bxWindow.bxMb = moltenBundleConfig()
    initMoltenBundleClient()
  }

  function moltenBundleConfig () {
    const meta = toValue(metaData)
    const page = toValue(pageMeta)
    const adKeywords: string[] = meta?.adKeywords ? [...meta.adKeywords] : []
    if (meta?.hasVideo) adKeywords.push('nonsticky')

    return {
      siteType: deviceType.value,
      adZone: (meta?.adaAutoNative || meta?.disallowAds) ? '_noad' : meta?.adAAdZone,
      indexPage: page?.pageType === 'structureterm',
      advertorial: meta?.advertorial,
      adKeywords,
      adSlots: meta?.adaAutoNative ? registerSlots.value.filter(slot => autonativeSlots.includes(slot)) : registerSlots.value,
      autonative: !!meta?.adaAutoNative,
      nudeContent: !!meta?.nudeContent,
      trackingPixelIdContent: meta?.adaTrackingPixelIdContent || null,
      lastAdSlotLoadedTime: null,
      asideEnabled: !!adaStore.asideEnabled,
      autoReloadEnabled: autoReloadEnabled.value,
      startAutoReloadInterval,
      initSlotStatus,
      ncbTopSlotsStatus: { teaser_11: false, teaser_12: false, teaser_13: false },
      adAAdKeywords: meta?.adAAdKeywords
    }
  }

  function initMoltenBundleClient () {
    bxWindow.MoltenBundle = bxWindow.MoltenBundle || {}
    bxWindow.MoltenBundle.cmd = bxWindow.MoltenBundle.cmd || []

    bxWindow.MoltenBundle.cmd.push(async function () {
      const bxMb = bxWindow.bxMb
      bxWindow.MoltenBundle.getClient().setSiteType(bxMb.siteType)
      bxWindow.MoltenBundle.getClient().setZone(bxMb.adZone)
      if (!bxMb.autonative) {
        bxWindow.MoltenBundle.getClient().setIsIndexPage(bxMb.indexPage)
        bxWindow.MoltenBundle.getClient().addTargetingParameter('keywords', bxMb.adKeywords)
      }
      // Trigger teaser backfill for sidebar (teaser_20 to 23) via keyword sidebar_art
      if (bxMb.asideEnabled && (window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth) >= 1000) {
        bxWindow.MoltenBundle.getClient().addTargetingParameter('ct', 'sidebar_art')
      }
      if (bxMb.nudeContent) {
        bxWindow.MoltenBundle.getClient().setKeywords(['nude_content'])
      }
      if (bxMb.adAAdKeywords) {
        bxWindow.MoltenBundle.getClient().setKeywords(bxMb.adAAdKeywords)
      }

      // pushing all ad slots still happens right away (same total requests, same order),
      // we just yield between chunks so this doesn't block an in-flight user interaction
      for (let i = 0; i < bxMb.adSlots.length; i += AD_SLOT_PUSH_CHUNK_SIZE) {
        bxMb.adSlots.slice(i, i + AD_SLOT_PUSH_CHUNK_SIZE).forEach((slot: string) => {
          bxWindow.MoltenBundle.push(slot)
        })
        if (i + AD_SLOT_PUSH_CHUNK_SIZE < bxMb.adSlots.length) {
          await yieldToMain()
        }
      }
      bxWindow.MoltenBundle.cmd.push(function () {
        bxWindow.MoltenBundle.finalize()
      })
      bxWindow.MoltenBundle.registerAdPositionLoadedHandler(function (position: MoltenBundlePosition) {
        const slotName = position.getName()
        const containsAd = position.containsAd()
        const dynamicSlotList = bxWindow.bxMb.clientConfig?.slotList

        // On Advertorial pages, we need to adjust the classes of dynamic AdSlots since we do not fill the slots with ads and would show empty spaces
        if (bxWindow.bxMb.advertorial && dynamicSlotList?.includes(slotName) && !containsAd) {
          // remove collection classes, while there is an empty ad container (Desktop Ads)
          document.querySelector(`#${slotName}`)?.closest('.bx-collection.bx-ada--slot-wrapper')?.classList.remove('bx-collection', 'bx-ada--slot-wrapper')
          // unset min-height of empty ad slot wrapper (Mobile and Desktop)
          document.querySelector(`.bx-ada--${slotName}`)?.classList.add('bx-ada--no-min-height')
        }

        // remove specific ncb fallback teaser if corresponding adSlot is loaded
        if (slotName.includes('teaser') && containsAd && document.querySelector(`#${slotName}_fallback`)) {
          (document.querySelector(`#${slotName}_fallback`) as HTMLElement).style.display = 'none'
        }
        if (['teaser_11', 'teaser_12', 'teaser_13'].includes(slotName) && document.querySelector(`#${slotName}_fallback`)) {
          // track slot status to hide fallbacks if needed when all ncb top slots are processed
          bxWindow.bxMb.ncbTopSlotsStatus[slotName] = containsAd ? 'loaded' : 'fallback'
          const isLastSlot = Object.values(bxWindow.bxMb.ncbTopSlotsStatus).filter((value: unknown) => value === false).length === 0
          if (isLastSlot) {
            const loadedSlots = Object.values(bxWindow.bxMb.ncbTopSlotsStatus).filter((value: unknown) => value === 'loaded').length
            if (loadedSlots === 0) {
              // if no ads were loaded, we hide the complete ncb top container instead of single teaser to prevent a layout gap
              (document.querySelector('[data-ncb-position="top"]') as HTMLElement).style.display = 'none'
            } else if (loadedSlots < 3 && bxWindow.bxMb.siteType === 'mobile') {
              // if we have at least on slot loaded with ads, we don't want any Fallback on Mobile
              Object.entries(bxWindow.bxMb.ncbTopSlotsStatus).forEach(([key, value]) => {
                if (value === 'fallback') {
                  (document.querySelector(`[data-ad-slot-id=${key}]`) as HTMLElement).style.display = 'none'
                }
              })
            }
          }
        }
        // identify medium rectangle fallbacks for teaser_14 to teaser_19 and mark them to attach additional styles
        if (['teaser_14', 'teaser_15', 'teaser_16', 'teaser_17', 'teaser_18', 'teaser_19'].includes(slotName) && containsAd) {
          setTimeout(() => {
            if (!document.querySelector(`#${slotName} .emsTeaserContainer`)) {
              document.querySelector(`#${slotName}`)?.parentElement?.classList.add('bx-ada--teaser-rectangle')
            }
          }, 1000)
        }
        // if there are further ad slots which are not teaser and not containing an ad,
        // we set the min-height to 0 to overrule the ad-size-predictions min-height, preventing large white space
        if (!containsAd && !slotName.includes('teaser') && !slotName.includes('mobile_1')) {
          (document.querySelector(`[data-ad-slot-id=${slotName}]`) as HTMLElement).style.minHeight = '0'
        }
        const timeNow = Math.round(+new Date() / 1000)
        if (bxWindow.bxMb.autoReloadEnabled && !bxWindow.bxMb.autonative && bxWindow.bxMb.lastAdSlotLoadedTime !== timeNow && slotName !== 'skyscraper_3') {
          bxWindow.bxMb.lastAdSlotLoadedTime = timeNow
          bxWindow.bxMb.initSlotStatus()
          bxWindow.bxMb.startAutoReloadInterval()
        }
      })
      if (bxWindow.bxMb.trackingPixelIdContent) {
        bxWindow.MoltenBundle.createTrackingPixel(bxWindow.bxMb.trackingPixelIdContent)
      }
    })
  }

  function startAutoReloadInterval () {
    clearInterval(autoReload.value.reloadIntervalId ?? undefined)
    autoReload.value.reloadIntervalId = setInterval(triggerAutoReload, autoReload.value.reloadInterval * 1000)
  }

  function triggerAutoReload () {
    const timeNow = Math.round(+new Date() / 1000)
    const activityWithinReloadInterval = timeNow - (autoReload.value.lastActivityTime ?? 0) < autoReload.value.reloadInterval
    const documentIsVisible = document.visibilityState === 'visible'
    const noDialogIsOpen = !dialogStore.isDialogOpen
    const lastLoadElapsedTimeGreaterThanInterval = timeNow - (bxWindow.bxMb.lastAdSlotLoadedTime ?? 0) >= autoReload.value.reloadInterval
    if (activityWithinReloadInterval && documentIsVisible && noDialogIsOpen && lastLoadElapsedTimeGreaterThanInterval) {
      const reloadSlotsWithViewability = reloadSlots.value.automatically.filter(slot => {
        const { hasViewability, isVisible } = autoReload.value.slotStatus[slot]
        return hasViewability && isVisible
      })
      if (reloadSlotsWithViewability.length > 0) {
        bxWindow.MoltenBundle.getClient().reloadSpecificAds(reloadSlotsWithViewability)
      }
    }
  }

  function initAutoReload () {
    reloadSlots.value.automatically = registerSlots.value.filter(slot => autoReload.value.allowedSlots.includes(slot))
    if (autoReloadEnabled.value) {
      initSlotStatus()
      initObserver()
      updateActivityTime()
      toggleActivityListeners()
    }
  }

  function initSlotStatus () {
    autoReload.value.slotStatus = {}
    reloadSlots.value.automatically.forEach(slot => {
      autoReload.value.slotStatus[slot] = { hasViewability: false, isVisible: false }
    })
  }

  function initObserver () {
    const visibleObserver = new IntersectionObserver(visibleObserverCallback, {
      root: null,
      rootMargin: '0px',
      threshold: 0.01
    })
    const viewabilityObserver = new IntersectionObserver(viewabilityObserverCallback, {
      root: null,
      rootMargin: '0px',
      threshold: autoReload.value.viewability.percentage / 100
    })
    const targets = document.querySelectorAll(reloadSlots.value.automatically.map(id => `#${id}`).join(', '))
    targets.forEach(target => {
      visibleObserver.observe(target)
      viewabilityObserver.observe(target)
    })
  }

  function visibleObserverCallback (entries: IntersectionObserverEntry[]) {
    entries.forEach(entry => {
      autoReload.value.slotStatus[entry.target.id].isVisible = entry.isIntersecting
    })
  }

  function viewabilityObserverCallback (entries: IntersectionObserverEntry[]) {
    entries.forEach(entry => {
      if (entry.intersectionRatio >= autoReload.value.viewability.percentage / 100 && entry.time >= autoReload.value.viewability.time * 1000) {
        autoReload.value.slotStatus[entry.target.id].hasViewability = true
      }
    })
  }

  function updateActivityTime () {
    autoReload.value.lastActivityTime = Math.round(+new Date() / 1000)
  }

  function toggleActivityListeners (remove = false) {
    autoReload.value.activityEvents.forEach(event => {
      if (remove) {
        document.removeEventListener(event, updateActivityTime)
      } else {
        document.addEventListener(event, updateActivityTime)
      }
    })
  }

  function adaSlotReloadOnInteraction (params: AdSlotReloadConfig, onAdReloadCallback: (() => void) | false = false) {
    if (adaStore.blockAds || !bxWindow.MoltenBundle || typeof bxWindow.MoltenBundle?.getClient !== 'function') {
      return
    }
    if (!registerSlots.value.length) {
      registerSlots.value = bxWindow.bxMb.adSlots
    }
    if (!reloadSlots.value.onInteraction.length) {
      reloadSlots.value.onInteraction = registerSlots.value.filter((slot: string) => params.reloadAdaSlots?.includes(slot))
    }
    let reloadNow = false
    const timeNow = Math.round(+new Date() / 1000)
    if (params.clickCount > 0) {
      clickEventCounter.value++
    }
    reloadNow = shouldReloadAdsOnInteraction(params, clickEventCounter.value, timeNow - reloadTriggerTime)
    let slotsToReload = reloadSlots.value.onInteraction

    if (adaStore.asyncContentMode) {
      if (!adaStore.isAsyncContentReadyForAdReload) {
        asyncContentReloadParams.value = params
        return
      }
      if ((timeNow - reloadTriggerTime) > asyncContentReloadCooldown) {
        reloadNow = true
      } else {
        reloadNow = false
      }
      const registeredAdSlots: string[] = bxWindow.bxMb.adSlots
      const clientConfig = bxWindow.bxMb.clientConfig
      if (clientConfig) {
        const { slotList: dynamicSlotList, latestRegisteredDynamicAdSlots, latestAllocatedDynamicAdSlots } = clientConfig
        const registeredDynamicSlots = registeredAdSlots.filter(slot => dynamicSlotList.includes(slot) && latestAllocatedDynamicAdSlots.includes(slot) && !latestRegisteredDynamicAdSlots.includes(slot))
        slotsToReload = Array.from(new Set([...registeredDynamicSlots, ...reloadSlots.value.onInteraction]))
      }
    }

    if (reloadNow) {
      if (onAdReloadCallback && typeof onAdReloadCallback === 'function') {
        onAdReloadCallback()
      }
      bxWindow.MoltenBundle.getClient().reloadSpecificAds(slotsToReload)
      reloadTriggerTime = timeNow

      if (process.env.NODE_ENV !== 'production') {
        console.groupCollapsed('reloadSlotsOnInteraction')
        console.table(slotsToReload)
        console.groupEnd()
      }
    }
  }

  return {
    reloadSlots,
    reloadTriggerTime,
    clickEventCounter,
    autoReload,
    autoReloadEnabled,
    asyncContentReloadParams,
    initMoltenBundle,
    moltenBundleConfig,
    startAutoReloadInterval,
    initAutoReload,
    initSlotStatus,
    adaSlotReloadOnInteraction
  }
}
